import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CalendarOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
dayjs.extend(updateLocale);
dayjs.updateLocale('en', { weekStart: 1 });

import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../shared/PullToRefreshIndicator';
import ScheduleTabSkeleton from './skeletons/ScheduleTabSkeleton';
import {
  providerScheduleApi,
  scheduleApi,
  providerMeApi,
  bookingApi,
} from '../../api/providerMeApi';
import { telegram } from '../../utils/telegram';
import type {
  ProviderSchedule,
  ScheduleDayEntry,
  ScheduleBreakEntry,
  ScheduleException,
  DayOfWeek,
  OffDateMode,
} from '../../types/provider';

import AvailabilityCard from './schedule/AvailabilityCard';
import NotConfiguredBanner from './schedule/NotConfiguredBanner';
import SectionHeader from './schedule/SectionHeader';
import DayRow from './schedule/DayRow';
import OffDateRow from './schedule/OffDateRow';
import OffDateAddCard from './schedule/OffDateAddCard';
import DayEditorSheet from './schedule/DayEditorSheet';
import ConfirmSheet from './shared/ConfirmSheet';

const DAY_ORDER: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const DAY_NUM: Record<DayOfWeek, number> = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
};

const fmtTime = (t: string) => t.slice(0, 5);

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const r = (err as { response?: { data?: { message?: string } } }).response;
    return r?.data?.message ?? fallback;
  }
  return fallback;
}

export default function ScheduleTab() {
  const { t } = useTranslation(['common', 'provider']);
  const dayName = (d: DayOfWeek) => t(`provider:schedule.days.${d}`, { defaultValue: d });

  const [schedule, setSchedule] = useState<ProviderSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [availLoading, setAvailLoading] = useState(false);
  const [providerId, setProviderId] = useState<number | null>(null);

  const [editingDay, setEditingDay] = useState<ScheduleDayEntry | null>(null);
  const [modalSaving, setModalSaving] = useState(false);

  const [breakSaving, setBreakSaving] = useState(false);

  const [deletingBreak, setDeletingBreak] = useState<ScheduleBreakEntry | null>(null);
  const [deletingException, setDeletingException] = useState<ScheduleException | null>(null);

  // Off date form
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [offDateAdding, setOffDateAdding] = useState(false);
  const [offDateMode, setOffDateMode] = useState<OffDateMode>('full');
  const [newOffStartTime, setNewOffStartTime] = useState<string | null>(null);
  const [newOffEndTime, setNewOffEndTime] = useState<string | null>(null);

  // Overlap warning
  const [overlapInfo, setOverlapInfo] = useState<{ count: number } | null>(null);

  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const profile = await providerMeApi.getProfile();
      setProviderId(profile.id);
      const [sched, excs] = await Promise.all([
        providerScheduleApi.getSchedule(),
        scheduleApi.getExceptions(profile.id),
      ]);
      setSchedule(sched);
      setExceptions(excs);
    } catch {
      message.error(t('provider:schedule.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const refreshSchedule = useCallback(async () => {
    try {
      const sched = await providerScheduleApi.getSchedule();
      setSchedule(sched);
    } catch {
      message.error(t('provider:schedule.refreshFailed'));
    }
  }, [t]);

  const refreshExceptions = useCallback(
    async (pid: number) => {
      try {
        const excs = await scheduleApi.getExceptions(pid);
        setExceptions(excs);
      } catch {
        message.error(t('provider:schedule.refreshFailed'));
      }
    },
    [t],
  );

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const { containerRef, pullDistance, isRefreshing, progress } = usePullToRefresh<HTMLDivElement>({
    onRefresh: loadData,
    disabled: loading,
  });

  // ─── Availability ─────────────────────────────────────────────────────
  const handleAvailToggle = async (enabled: boolean) => {
    setAvailLoading(true);
    try {
      await providerMeApi.setActive(enabled);
      telegram.hapticFeedback.impact('light');
      setSchedule((prev) => (prev ? { ...prev, availabilityEnabled: enabled } : prev));
    } catch {
      message.error(t('provider:schedule.genericError'));
    } finally {
      setAvailLoading(false);
    }
  };

  // ─── Day toggle ───────────────────────────────────────────────────────
  const handleDayToggle = async (day: ScheduleDayEntry) => {
    if (day.workingHoursId != null) {
      try {
        await providerScheduleApi.updateWorkingHours(day.workingHoursId, { enabled: !day.enabled });
        telegram.hapticFeedback.impact('light');
        await refreshSchedule();
      } catch {
        message.error(t('provider:schedule.genericError'));
      }
    } else {
      try {
        await providerScheduleApi.upsertWorkingHours({
          dayOfWeek: day.dayOfWeek,
          startTime: '09:00',
          endTime: '18:00',
        });
        telegram.hapticFeedback.impact('light');
        await refreshSchedule();
      } catch (err: unknown) {
        message.error(getApiErrorMessage(err, t('provider:schedule.addFailed')));
      }
    }
  };

  // ─── Day editor ───────────────────────────────────────────────────────
  const handleEditDay = (day: ScheduleDayEntry) => {
    setEditingDay(day);
  };

  const handleCloseEditor = () => {
    if (modalSaving || breakSaving) return;
    setEditingDay(null);
  };

  const handleSaveHours = async (startTime: string, endTime: string) => {
    if (!editingDay) return;
    setModalSaving(true);
    try {
      if (editingDay.workingHoursId != null) {
        await providerScheduleApi.updateWorkingHours(editingDay.workingHoursId, {
          startTime,
          endTime,
        });
      } else {
        await providerScheduleApi.upsertWorkingHours({
          dayOfWeek: editingDay.dayOfWeek,
          startTime,
          endTime,
        });
      }
      telegram.hapticFeedback.notification('success');
      setEditingDay(null);
      await refreshSchedule();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, t('provider:schedule.saveFailed')));
    } finally {
      setModalSaving(false);
    }
  };

  const handleAddBreak = async (startTime: string, endTime: string) => {
    if (!editingDay || !providerId) return;
    setBreakSaving(true);
    try {
      await scheduleApi.createBreak({
        serviceProviderId: providerId,
        dayOfWeek: DAY_NUM[editingDay.dayOfWeek],
        startTime,
        endTime,
      });
      telegram.hapticFeedback.notification('success');
      const sched = await providerScheduleApi.getSchedule();
      setSchedule(sched);
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, t('provider:schedule.addFailed')));
    } finally {
      setBreakSaving(false);
    }
  };

  const handleDeleteBreak = async (breakItem: ScheduleBreakEntry) => {
    try {
      await scheduleApi.deleteBreak(breakItem.id);
      telegram.hapticFeedback.notification('success');
      const sched = await providerScheduleApi.getSchedule();
      setSchedule(sched);
    } catch {
      message.error(t('provider:schedule.deleteFailed'));
    } finally {
      setDeletingBreak(null);
    }
  };

  // ─── Off date ─────────────────────────────────────────────────────────
  const resetOffDateForm = () => {
    setNewDate('');
    setNewReason('');
    setOffDateMode('full');
    setNewOffStartTime(null);
    setNewOffEndTime(null);
  };

  const performAddOffDate = async () => {
    if (!providerId) return;
    await scheduleApi.createException({
      serviceProviderId: providerId,
      exceptionDate: newDate,
      reason: newReason || undefined,
      ...(offDateMode === 'partial' && newOffStartTime && newOffEndTime
        ? { startTime: newOffStartTime, endTime: newOffEndTime }
        : {}),
    });
  };

  const handleAddOffDate = async () => {
    if (!newDate) return;
    if (offDateMode === 'partial') {
      if (!newOffStartTime || !newOffEndTime) {
        message.warning(t('provider:schedule.offDates.partialRequiresTimes'));
        return;
      }
      if (newOffStartTime >= newOffEndTime) {
        message.warning(t('provider:schedule.offDates.invalidTime'));
        return;
      }
    }
    if (!providerId) {
      message.error(t('provider:schedule.profileNotLoaded'));
      return;
    }

    setOffDateAdding(true);

    // Overlap check
    let overlapCount = 0;
    try {
      const bookings = await bookingApi.getByDate(newDate);
      if (bookings.totalElements > 0) overlapCount = bookings.totalElements;
    } catch {
      // ignore — proceed without overlap warning
    }

    if (overlapCount > 0) {
      setOffDateAdding(false);
      setOverlapInfo({ count: overlapCount });
      return;
    }

    try {
      await performAddOffDate();
      telegram.hapticFeedback.notification('success');
      resetOffDateForm();
      await refreshExceptions(providerId);
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, t('provider:schedule.addFailed')));
    } finally {
      setOffDateAdding(false);
    }
  };

  const handleOverlapConfirm = async () => {
    if (!providerId) return;
    setOffDateAdding(true);
    try {
      await performAddOffDate();
      telegram.hapticFeedback.notification('success');
      resetOffDateForm();
      await refreshExceptions(providerId);
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, t('provider:schedule.addFailed')));
    } finally {
      setOffDateAdding(false);
      setOverlapInfo(null);
    }
  };

  const handleDeleteException = async (item: ScheduleException) => {
    try {
      await scheduleApi.deleteException(item.id);
      telegram.hapticFeedback.notification('success');
      if (providerId) await refreshExceptions(providerId);
    } catch {
      message.error(t('provider:schedule.deleteFailed'));
    } finally {
      setDeletingException(null);
    }
  };

  const isNotConfigured = !schedule?.scheduleConfigured;
  const editingDayBreaks = editingDay
    ? (schedule?.breaks ?? []).filter((b) => b.dayOfWeek === editingDay.dayOfWeek)
    : [];

  return (
    <div ref={containerRef} style={{ height: '100%', overflowY: 'auto', position: 'relative' }}>
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
        progress={progress}
      />
      <div style={{ padding: 16, paddingBottom: 32 }}>
        {loading && <ScheduleTabSkeleton />}

        {!loading && (
          <div key="ready" className="bl-animate-in">
            {/* 1. Availability */}
            <AvailabilityCard
              enabled={schedule?.availabilityEnabled ?? false}
              loading={availLoading}
              onToggle={handleAvailToggle}
            />

            {/* 2. Not configured banner */}
            {isNotConfigured && <NotConfiguredBanner />}

            {/* 3. Weekly schedule */}
            <div style={{ marginBottom: 16 }}>
              <SectionHeader
                icon={<CalendarOutlined />}
                iconColor="#3390ec"
                title={t('provider:schedule.weekly.title')}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {DAY_ORDER.map((dow) => {
                  const day = schedule?.weeklySchedule.find((d) => d.dayOfWeek === dow);
                  if (!day) return null;
                  const dayBreaks = (schedule?.breaks ?? []).filter((b) => b.dayOfWeek === dow);
                  return (
                    <DayRow
                      key={dow}
                      day={day}
                      dayLabel={dayName(dow)}
                      breaks={dayBreaks}
                      onToggle={() => void handleDayToggle(day)}
                      onEdit={() => handleEditDay(day)}
                    />
                  );
                })}
              </div>
            </div>

            {/* 4. Off-dates */}
            <div>
              <SectionHeader
                icon={<CloseCircleOutlined />}
                iconColor="#ef4444"
                title={t('provider:schedule.offDates.title')}
              />

              <OffDateAddCard
                date={newDate}
                reason={newReason}
                mode={offDateMode}
                startTime={newOffStartTime}
                endTime={newOffEndTime}
                saving={offDateAdding}
                onDateChange={setNewDate}
                onReasonChange={setNewReason}
                onModeChange={setOffDateMode}
                onStartTimeChange={setNewOffStartTime}
                onEndTimeChange={setNewOffEndTime}
                onSubmit={() => void handleAddOffDate()}
              />

              {!exceptions.length ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '24px 16px',
                    background: 'var(--bl-surface, #fff)',
                    border: '1px dashed var(--bl-divider, #ebebeb)',
                    borderRadius: 14,
                    color: 'var(--bl-text-muted, #9ca3af)',
                    fontSize: 13,
                  }}
                >
                  {t('provider:schedule.offDates.empty')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {exceptions.map((item) => (
                    <OffDateRow
                      key={item.id}
                      item={item}
                      dayLabel={dayName(item.dayOfWeek)}
                      onDelete={() => setDeletingException(item)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Day editor bottom sheet */}
      <DayEditorSheet
        day={editingDay}
        dayLabel={editingDay ? dayName(editingDay.dayOfWeek) : ''}
        breaks={editingDayBreaks}
        saving={modalSaving}
        breakSaving={breakSaving}
        onClose={handleCloseEditor}
        onSaveHours={handleSaveHours}
        onAddBreak={handleAddBreak}
        onDeleteBreak={(b) => setDeletingBreak(b)}
      />

      {/* Break delete confirm */}
      <ConfirmSheet
        open={deletingBreak !== null}
        title={t('provider:schedule.breaks.deleteSheetTitle')}
        description={
          deletingBreak
            ? t('provider:schedule.breaks.deleteSheetDesc', {
                range: `${fmtTime(deletingBreak.startTime)}–${fmtTime(deletingBreak.endTime)}`,
              })
            : ''
        }
        confirmText={t('provider:schedule.breaks.deleteSheetConfirm')}
        cancelText={t('provider:schedule.breaks.sheetCancel')}
        danger
        onConfirm={() => deletingBreak && void handleDeleteBreak(deletingBreak)}
        onCancel={() => setDeletingBreak(null)}
      />

      {/* Exception delete confirm */}
      <ConfirmSheet
        open={deletingException !== null}
        title={t('provider:schedule.offDates.deleteSheetTitle')}
        description={
          deletingException
            ? t('provider:schedule.offDates.deleteSheetDesc', {
                date: dayjs(deletingException.exceptionDate).format('D MMMM'),
              })
            : ''
        }
        confirmText={t('provider:schedule.offDates.deleteSheetConfirm')}
        cancelText={t('provider:schedule.breaks.sheetCancel')}
        danger
        onConfirm={() => deletingException && void handleDeleteException(deletingException)}
        onCancel={() => setDeletingException(null)}
      />

      {/* Overlap warning confirm */}
      <ConfirmSheet
        open={overlapInfo !== null}
        title={t('provider:schedule.offDates.overlapTitle')}
        description={
          overlapInfo
            ? t('provider:schedule.offDates.overlapContent', { count: overlapInfo.count })
            : ''
        }
        confirmText={t('provider:schedule.offDates.overlapAdd')}
        cancelText={t('provider:schedule.offDates.overlapCancel')}
        danger={false}
        loading={offDateAdding}
        onConfirm={() => void handleOverlapConfirm()}
        onCancel={() => setOverlapInfo(null)}
      />
    </div>
  );
}
